import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BaseSelect, BaseTextInput } from "src/components";
import { DocumentProofRequest } from "../types";
import { onlyNumber, validatePhoneNumberBoolean, convertKoreanPhoneNumber } from "src/utils/common-util";
import { MbMember } from "src/api/member/member-types";
import i18n from "src/i18n";

type Props = {
  documentProofRequest: DocumentProofRequest;
  setDocumentProofRequest: (request: DocumentProofRequest) => void;
  identityNumError: string;
  setIdentityNumError: (error: string) => void;
  member?: MbMember | null;
};

// 신분증 타입 값 생성 함수
const valueMaker = (documentProofRequest: DocumentProofRequest) => {
  if (!documentProofRequest || !documentProofRequest.identityType) {
    return null;
  }
  if (documentProofRequest.identityType.includes("CARD_NUM")) {
    if (documentProofRequest.tradeUsage === "INCOME") {
      return "I_CARD_NUM";
    }
    if (documentProofRequest.tradeUsage === "EXPENSE") {
      return "E_CARD_NUM";
    }
  } else {
    return documentProofRequest.identityType;
  }
};

// 휴대폰 번호 변환 함수 (82로 시작하는 경우 0으로 변환, 숫자만 반환)
const convertPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";
  let result = phoneNumber.replace(/ /g, ""); // 공백제거
  result = result.split("-").join(""); // 데시제거

  // 82로 시작하는 경우 0으로 변환 (+82는 convertKoreanPhoneNumber에서 처리)
  if (result.startsWith("82") && !result.startsWith("820")) {
    result = "0" + result.substring(2);
  } else {
    // convertKoreanPhoneNumber를 사용해서 +82를 0으로 변환
    result = convertKoreanPhoneNumber(result);
    result = result.split("-").join(""); // 하이픈 제거
  }

  return onlyNumber(result);
};

// 신분증 번호 유효성 검사 함수
export const validateIdentityNum = (identityNum: string, identityType: string | null | undefined): string => {
  if (identityNum === "") {
    return i18n.t("detail.requiredField", { ns: "contract" });
  }

  if (identityType === "MOBILE_NUM" && !validatePhoneNumberBoolean(identityNum)) {
    return i18n.t("detail.invalidMobileFormat", { ns: "contract" });
  }

  if (identityType?.includes("CARD_NUM") && (identityNum.length < 13 || identityNum.length > 19)) {
    return i18n.t("detail.invalidCardFormat", { ns: "contract" });
  }

  if (identityType === "CORP_NUM" && identityNum.length !== 10) {
    return i18n.t("detail.invalidBusinessNumberFormat", { ns: "contract" });
  }

  return "";
};

// 현금영수증 발급 정보 입력 폼 컴포넌트
const CashBillForm = ({ documentProofRequest, setDocumentProofRequest, identityNumError, setIdentityNumError, member }: Props) => {
  const { t } = useTranslation("contract");
  // identityType이 MOBILE_NUM이고 identityNum이 비어있을 때 회원정보의 phoneNumber를 기본값으로 설정
  useEffect(() => {
    if (documentProofRequest?.identityType === "MOBILE_NUM" && member?.phoneNumber) {
      setDocumentProofRequest({
        ...documentProofRequest,
        identityNum: convertPhoneNumber(member.phoneNumber),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentProofRequest?.identityType, member?.phoneNumber]);

  // API에서 받아온 identityNum이 82로 시작하거나 MOBILE_NUM인 경우 항상 변환 확인
  useEffect(() => {
    if (documentProofRequest?.identityType === "MOBILE_NUM" && documentProofRequest?.identityNum) {
      const converted = convertPhoneNumber(documentProofRequest.identityNum);
      // 변환된 값이 다르면 state 업데이트
      if (converted !== documentProofRequest.identityNum) {
        setDocumentProofRequest({
          ...documentProofRequest,
          identityNum: converted,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentProofRequest?.identityNum, documentProofRequest?.identityType]);

  return (
    <article className="w-100">
      <section className="cashbill-info">
        <div className="base-section-layout__title-wrap">
          <h2 className="mypage_required">{t("detail.cashReceiptIssuanceType")}</h2>
        </div>
        <BaseSelect
          className="identity-type"
          wrapClassName="text-left"
          value={valueMaker(documentProofRequest)}
          options={[
            { label: t("detail.transactionType.personalMobile"), value: "MOBILE_NUM", type: "INCOME" },
            { label: t("detail.transactionType.personalCard"), value: "I_CARD_NUM", type: "INCOME" },
            { label: t("detail.transactionType.businessNumber"), value: "CORP_NUM", type: "EXPENSE" },
            { label: t("detail.transactionType.businessCard"), value: "E_CARD_NUM", type: "EXPENSE" },
          ]}
          getObj={true}
          onChange={(option: any) => {
            const isMobileNum = option.value === "MOBILE_NUM";
            setDocumentProofRequest({
              ...documentProofRequest,
              identityType: option.value.includes("CARD_NUM") ? "CARD_NUM" : option.value,
              tradeUsage: option.type,
              identityNum: isMobileNum && member?.phoneNumber ? convertPhoneNumber(member.phoneNumber) : "",
            } as DocumentProofRequest);
            setIdentityNumError("");
          }}
          placeholder={t("detail.applicationTypePlaceholder")}
        />
        <div className="base-section-layout__title-wrap pt30">
          <h2 className="mypage_required">{t("detail.documentationNumber")}</h2>
        </div>
        <BaseTextInput
          type="text"
          value={documentProofRequest?.identityNum || ""}
          onChange={(value: string | undefined) => {
            // 숫자만 입력되도록 필터링
            const numbersOnly = onlyNumber(String(value || ""));
            let convertedValue = numbersOnly;

            // MOBILE_NUM인 경우에만 휴대폰 번호 형식으로 변환 (11자리 제한)
            if (documentProofRequest?.identityType === "MOBILE_NUM") {
              convertedValue = convertPhoneNumber(numbersOnly.slice(0, 11));
            } else {
              // 그 외의 경우 19자리까지 허용
              convertedValue = numbersOnly.slice(0, 19);
            }

            setDocumentProofRequest({
              ...documentProofRequest,
              identityNum: convertedValue,
            });
            if (value) {
              setIdentityNumError("");
            }
          }}
          onBlur={() => {
            const error = validateIdentityNum(documentProofRequest?.identityNum || "", documentProofRequest?.identityType);
            setIdentityNumError(error);
          }}
          placeholder={t("detail.documentationNumberPlaceholder")}
          isInvalid={!!identityNumError}
        />
        {identityNumError && <p className="mt5 font12 text-red">{identityNumError}</p>}
      </section>
    </article>
  );
};

export default CashBillForm;
